import { useEffect, useState } from 'react';
import { supabase, hasSupabase } from './supabase';

// Generic subscription to a table. Returns the rows and a refetch fn.
// Optimistic inserts are handled by the screens themselves.
export function useRealtimeTable(table, { orderBy = 'created_at', ascending = false } = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refetch = async () => {
    if (!hasSupabase) {
      setLoading(false);
      return;
    }
    const { data } = await supabase.from(table).select('*').order(orderBy, { ascending });
    setRows(data || []);
    setLoading(false);
  };

  useEffect(() => {
    refetch();
    if (!hasSupabase) return;

    const channel = supabase
      .channel(`rt:${table}`)
      .on('postgres_changes', { event: '*', schema: 'public', table }, (payload) => {
        setRows((prev) => {
          if (payload.eventType === 'INSERT') {
            // Dedupe: if we already optimistically inserted the same row, skip.
            if (prev.some((r) => r.id === payload.new.id)) return prev;
            return [payload.new, ...prev];
          }
          if (payload.eventType === 'UPDATE')
            return prev.map((r) => (r.id === payload.new.id ? payload.new : r));
          if (payload.eventType === 'DELETE')
            return prev.filter((r) => r.id !== payload.old.id);
          return prev;
        });
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  return { rows, loading, refetch, setRows };
}
